import * as React from 'react';

import { cn } from '@rematter/ui-web';
import {
  Loading,
  Tree,
  TreeItem,
  TreeItemContent,
  TreeItemText,
} from '@rematter/ui-web';
import { cva, type VariantProps } from 'class-variance-authority';
import { useAtom, useAtomValue } from 'jotai';
import { Folder, FolderOpen } from 'lucide-react';

import { $currentFolderId } from '../../_state/files.state';
import * as state from '../../_state/fileTree.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FileRecord } from '../../types/files';

const treeNodeVariants = cva(
  [
    'flex w-full items-center gap-2 rounded text-left text-sm transition-colors',
    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      state: {
        default: '',
        selected: '',
        disabled: 'cursor-not-allowed opacity-50',
      },
      size: {
        default: 'px-1 py-0.5',
        compact: 'p-0.5',
        large: 'px-2 py-1',
      },
    },
    defaultVariants: {
      state: 'default',
      size: 'default',
    },
  },
);

const treeItemVariants = cva(['rounded-lg transition-colors'], {
  variants: {
    state: {
      default: 'hover:bg-neutral/10',
      selected: 'bg-primary/10 hover:bg-primary/20',
      disabled: '',
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const iconVariants = cva(
  ['flex items-center justify-center transition-colors'],
  {
    variants: {
      state: {
        default: 'text-primary/50',
        selected: 'text-primary-darker',
        empty: 'text-neutral/40',
      },
      size: {
        default: 'size-4',
        compact: 'size-3',
        large: 'size-5',
      },
    },
    defaultVariants: {
      state: 'default',
      size: 'default',
    },
  },
);

/**
 * Props for TreeNodeComponent styling variants
 */
interface TreeNodeProps extends VariantProps<typeof treeNodeVariants> {
  node: FileRecord;
  onAction?: (nodeId: string) => void;
  isSelected?: boolean;
  isDisabled?: boolean;
}

// Recursive component to render tree nodes
const TreeNodeComponent: React.FC<TreeNodeProps> = ({
  node,
  onAction,
  isDisabled = false,
  size = 'default',
}) => {
  const hasChildren = node.children.length > 0;
  const currentFolderId = useAtomValue($currentFolderId);
  const isSelected = currentFolderId === node.id;
  const nodeState = isDisabled
    ? 'disabled'
    : isSelected
      ? 'selected'
      : 'default';

  const iconState = isSelected ? 'selected' : 'default';

  return (
    <TreeItem
      id={node.id}
      textValue={node.name}
      onAction={() => onAction?.(node.id)}
      className={cn(
        '@TreeNodeComponent',
        treeItemVariants({ state: nodeState }),
      )}
    >
      <TreeItemContent>
        <div className="flex min-w-0 items-center gap-2">
          <button
            disabled={isDisabled}
            className={cn(
              'min-w-0',
              treeNodeVariants({
                state: nodeState,
                size,
              }),
            )}
          >
            <div className={iconVariants({ state: iconState, size })}>
              {hasChildren ? (
                <>
                  <Folder className="group-data-[expanded]:hidden" />
                  <FolderOpen className="hidden group-data-[expanded]:block" />
                </>
              ) : (
                <Folder />
              )}
            </div>
            <TreeItemText>{node.name}</TreeItemText>
          </button>
        </div>
      </TreeItemContent>
      {node.children?.map((child) => (
        <TreeNodeComponent
          key={child.id}
          node={child}
          size={size}
          isDisabled={isDisabled}
        />
      ))}
    </TreeItem>
  );
};

/**
 * Props for FileTree component
 */
interface FileTreeProps extends VariantProps<typeof treeNodeVariants> {
  isDisabled?: boolean;
}

export const FileTree: React.FC<FileTreeProps> = ({
  size = 'default',
  isDisabled = false,
}) => {
  const t = useTranslations();

  const [expandedDirectoryIds, setExpandedDirectoryIds] = useAtom(
    state.$expandedDirectoryIds,
  );
  const { data: rootDirectories } = useAtomValue(state.$directoryTree);
  const [currentFolderId, setCurrentFolderId] = useAtom($currentFolderId);

  const handleExpandedChange = (keys: Set<React.Key>) => {
    const expanded = [...keys]
      .filter((k) => k !== undefined)
      .filter((k) => k !== null)
      .filter((k) => k !== 'root')
      .map((k) => k.toString());

    setExpandedDirectoryIds(expanded);
  };

  const handleItemAction = (nodeId: React.Key) => {
    if (nodeId === 'root') {
      setCurrentFolderId(undefined);
    } else {
      setCurrentFolderId(nodeId.toString());
    }
  };

  if (!rootDirectories) {
    return <Loading isLoading={true} className="size-full" />;
  }

  return (
    <Tree
      aria-label="Directory Tree"
      selectionMode="none"
      onAction={handleItemAction}
      onExpandedChange={handleExpandedChange}
      defaultExpandedKeys={['root', ...expandedDirectoryIds]}
      className="@FileTree"
    >
      <TreeItem
        id="root"
        textValue={t.fileExplorer.tree.root}
        className={treeItemVariants({
          state: !currentFolderId ? 'selected' : 'default',
        })}
      >
        <TreeItemContent>
          <button
            disabled={isDisabled}
            className={cn(
              'min-w-0',
              treeNodeVariants({
                state: isDisabled ? 'disabled' : 'default',
                size,
              }),
            )}
          >
            <div
              className={iconVariants({
                state: !currentFolderId ? 'selected' : 'default',
                size,
              })}
            >
              <FolderOpen />
            </div>
            <TreeItemText>{t.fileExplorer.tree.root}</TreeItemText>
          </button>
        </TreeItemContent>
        {rootDirectories.map((node) => (
          <TreeNodeComponent
            onAction={handleItemAction}
            key={node.id}
            node={node}
            size={size}
            isDisabled={isDisabled}
          />
        ))}
      </TreeItem>
    </Tree>
  );
};

export { treeNodeVariants, treeItemVariants, iconVariants };
export type { TreeNodeProps, FileTreeProps };
